import { useQuery } from '@tanstack/react-query';

import map from 'lodash/map';
import keyBy from 'lodash/keyBy';

import { getRequest, paginationMapper, serialize } from 'src/utils/request';
import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { buildChild, buildChildren, buildAttributes } from 'src/utils/tools';

type IncludedDataResponse<
  TDataAttributes = unknown,
  TIncluded = unknown,
  TMeta = unknown,
> = {
  data: Array<{
    id: string;
    type: string;
    attributes: TDataAttributes;
    relationships: {
      type: { data: { id: string; type: string } };
      category: { data: { id: string; type: string } };
      options: { data: Array<{ id: string; type: string }> };
    };
  }>;
  included: Array<{
    id: string;
    type: string;
    attributes: TIncluded;
  }>;
  meta: TMeta;
  links: UCM.DynamicFieldLinkType;
};

export const mapDynamicField = (
  fields: IncludedDataResponse<UCM.DynamicFieldRawType>['data'],
  included: Record<
    string,
    { id: string; type: string; attributes: UCM.DynamicFieldIncludedRawType }
  >,
): Array<UCM.ExtendedDynamicFieldType> => {
  return map(fields, (field) => {
    const category = buildChild(
      field,
      included,
      'category',
    ) as UCM.ExtendedDynamicFieldType['category'];

    const options = buildChildren(
      field,
      included,
      'options',
    ) as UCM.ExtendedDynamicFieldType['options'];

    const type = buildChild(
      field,
      included,
      'type',
    ) as UCM.ExtendedDynamicFieldType['type'];

    const dynamicField = buildAttributes(field) as UCM.ExtendedDynamicFieldType;

    dynamicField.category = category;
    dynamicField.options = options;
    dynamicField.type = type;

    return dynamicField;
  });
};

const getDynamicFields = async (
  fieldCategoryUuid: string,
  params: Record<string, string | number>,
) => {
  const query = serialize(params);

  const { data } = await getRequest<
    IncludedDataResponse<
      UCM.DynamicFieldRawType,
      UCM.DynamicFieldIncludedRawType,
      UCM.MetaRawType
    >
  >(`${API.GET.dynamicFields(fieldCategoryUuid)}?${query}`, {
    headers: { Accept: 'application/vnd.falcon.v1+json' },
  });

  const included = keyBy(data.included, (item) => `${item.id}-${item.type}`);

  return {
    meta: paginationMapper(data.meta),
    data: mapDynamicField(data.data, included),
  };
};

export const useGetDynamicFields = (
  fieldCategoryUuid: string,
  params: Record<string, string | number>,
  config: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DYNAMIC_FIELDS, fieldCategoryUuid, params],
    queryFn: () => getDynamicFields(fieldCategoryUuid, params),
    staleTime: 1000 * 60 * 5,
    ...config,
  });
};
